import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import React, { useEffect, useRef, useState } from 'react';
import { NoData } from '../MarketEffect.styles';
import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';
import {
	ChartWrapper,
	LoadingContainer,
	PriceIndexWrapper,
	SpendIndexWrapper,
} from './Charts.styles';

const priceChartInitialOptions = {
	chart: {
		type: 'line',
		height: '30%',
	},
	exporting: {
		enabled: false,
		allowHTML: true,
	},
	credits: {
		enabled: false,
	},
	title: {
		text: '',
	},
	legend: {
		align: 'center',
		padding: 0,
	},
	xAxis: {
		title: {
			text: 'Year',
		},
		type: 'datetime',
		crosshair: true,
		lineColor: '#000000',
		labels: {
			style: {
				fontSize: '11px',
				fontWeight: '400',
				lineHeight: '13px',
				letterSpacing: 0,
				color: '#333333',
			},
		},
		gridLineWidth: 0,
	},
	yAxis: {
		title: {
			text: 'Weight Index',
		},
		gridLineWidth: 0,
		labels: {
			style: {
				fontSize: '11px',
				fontWeight: '400',
				lineHeight: '13px',
				letterSpacing: 0,
				color: '#333333',
			},
		},
		lineColor: '#000000',
		lineWidth: 1,
	},
	tooltip: {
		shared: true,
		useHTML: true,
	},
	plotOptions: {
		column: {
			pointPadding: 0.2,
			borderWidth: 0,
		},
	},
	colors: ['#032E45', '#448EB4', '#45B9F7', '#AFE1FC'],
};

const spendChartInitialOptions = {
	chart: {
		type: 'line',
		height: '30%',
	},
	exporting: {
		enabled: false,
		allowHTML: true,
	},
	credits: {
		enabled: false,
	},
	title: {
		text: '',
	},
	legend: {
		align: 'center',
		padding: 0,
	},
	xAxis: {
		title: {
			text: 'Year',
		},
		type: 'datetime',
		crosshair: true,
		lineColor: '#000000',
		labels: {
			style: {
				fontSize: '11px',
				fontWeight: '400',
				lineHeight: '13px',
				letterSpacing: 0,
				color: '#333333',
			},
		},
		gridLineWidth: 0,
	},
	yAxis: {
		title: {
			text: 'Spend Index',
		},
		gridLineWidth: 0,
		labels: {
			style: {
				fontSize: '11px',
				fontWeight: '400',
				lineHeight: '13px',
				letterSpacing: 0,
				color: '#333333',
			},
		},
		lineColor: '#000000',
		lineWidth: 1,
	},
	tooltip: {
		shared: true,
		useHTML: true,
	},
	plotOptions: {
		column: {
			pointPadding: 0.2,
			borderWidth: 0,
		},
	},
	colors: ['#032E45', '#448EB4', '#45B9F7', '#AFE1FC'],
};

const getYearlyTimestamp = year => {
	return new Date(year, 0).getTime(); // Jan 1st of the given year
};

const Annually = ({ chartData, isChartLoading }) => {
	const [data, setData] = useState([]);
	const [priceIndexChartOptions, setPriceIndexChartOptions] = useState({});
	const [spendIndexChartOptions, setSpendIndexChartOptions] = useState({});
	const priceIndexRefYearly = useRef();
	const spendIndexRefYearly = useRef();

	useEffect(
		() => {
			if (chartData.length > 0) {
				const {
					marketAnalysisPriceIndexChartData,
					marketAnalysisSpendIndexChartData,
				} = getSpendAndPriceDataForChart(chartData);
				setPriceIndexChartOptions({
					...priceChartInitialOptions,
					...{ series: marketAnalysisPriceIndexChartData },
				});
				setSpendIndexChartOptions({
					...spendChartInitialOptions,
					...{ series: marketAnalysisSpendIndexChartData },
				});
				setData(chartData);
			}
		}, // eslint-disable-next-line
		[chartData]
	);

	const sortByYearAndMonth = data => {
		return data.sort((a, b) => {
			if (a.pO_Year === b.pO_Year) {
				return a.pO_Month - b.pO_Month;
			}
			return a.pO_Year - b.pO_Year;
		});
	};

	const getSpendAndPriceDataForChart = data => {
		const sortedResult = sortByYearAndMonth(data);
		let result = {
			marketAnalysisPriceIndexChartData: [],
			marketAnalysisSpendIndexChartData: [],
		};
		result.marketAnalysisPriceIndexChartData = Object.values(
			sortedResult.reduce((acc, item) => {
				const timestamp = getYearlyTimestamp(item.pO_Year);
				if (!acc[item.supplier]) {
					acc[item.supplier] = {
						type: 'line',
						name: item.supplier,
						data: [],
						additionalInfo: {
							year: item.pO_Year,
						},
					};
				}
				acc[item.supplier].data.push([timestamp, item.total_price_index]);
				return acc;
			}, {})
		);

		result.marketAnalysisSpendIndexChartData = Object.values(
			sortedResult.reduce((acc, item) => {
				const timestamp = getYearlyTimestamp(item.pO_Year);
				if (!acc[item.supplier]) {
					acc[item.supplier] = {
						type: 'line',
						name: item.supplier,
						data: [],
						additionalInfo: {
							year: item.pO_Year,
						},
					};
				}
				acc[item.supplier].data.push([timestamp, item.total_spend_index]);
				return acc;
			}, {})
		);

		return result;
	};

	const Loader = () => (
		<LoadingContainer>
			<ApLoaderDefault loaderText={() => <ApLoaderText />} />
		</LoadingContainer>
	);

	return (
		<div>
			{isChartLoading ? (
				<Loader />
			) : (
				<>
					{data.length === 0 ? (
						<NoData>No data available.</NoData>
					) : (
						<ChartWrapper>
							<PriceIndexWrapper>
								<HighchartsReact
									highcharts={Highcharts}
									options={priceIndexChartOptions}
									ref={priceIndexRefYearly}
								/>
							</PriceIndexWrapper>
							<SpendIndexWrapper>
								<div>
									<HighchartsReact
										highcharts={Highcharts}
										options={spendIndexChartOptions}
										ref={spendIndexRefYearly}
									/>
								</div>
							</SpendIndexWrapper>
						</ChartWrapper>
					)}
				</>
			)}
		</div>
	);
};

export default React.memo(Annually);
